import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { printPage } from "../../lib/print";
import { updateIncidentReport, listReportAttachments, reportAttachmentViewUrl, getIncidentTypes } from "../../lib/api";
import ReportAttachments from "../reports/ReportAttachments";

const MOVED_CLASS_TYPE_ID = "moved_class_disruption";

const REPORT_TYPES = [
  { value: "clinical", label: "Clinical Report" },
  { value: "incident", label: "Incident Report" },
  { value: "general_note", label: "General Note" },
];

// Academic year runs Aug 1 → Jul 31. A date in Aug–Dec belongs to that
// calendar year's school year; Jan–Jul belongs to the previous year's.
const schoolYearStart = (dateStr) => {
  if (!dateStr || dateStr.length < 7) return null;
  const y = parseInt(dateStr.slice(0, 4), 10);
  const m = parseInt(dateStr.slice(5, 7), 10);
  if (!y || !m) return null;
  return m >= 8 ? y : y - 1;
};
const schoolYearLabel = (startYear) => `${startYear}\u2013${startYear + 1}`;
const currentSchoolYearStart = () => schoolYearStart(new Date().toISOString().slice(0, 10));

export function IncidentsTab({ student, reports, accidentReports, hasRole, onUpdated }) {
  const canEdit = hasRole("Admin") || hasRole("Clinician");
  const [editReport, setEditReport] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openHistory, setOpenHistory] = useState({});
  const [schoolYear, setSchoolYear] = useState(() => String(currentSchoolYearStart()));
  const [sortOrder, setSortOrder] = useState("newest");
  const [incidentTypes, setIncidentTypes] = useState([]);

  useEffect(() => {
    getIncidentTypes().then((r) => setIncidentTypes(r.data || [])).catch(() => {});
  }, []);

  const typeMap = React.useMemo(() => {
    const m = {};
    incidentTypes.forEach((t) => { m[t.id] = t.label; });
    return m;
  }, [incidentTypes]);
  const typeLabel = (id) => (id ? (typeMap[id] || id) : "");

  const classMovesThisYear = React.useMemo(() => {
    const cy = currentSchoolYearStart();
    return (reports || []).filter(
      (r) => r.incident_type === MOVED_CLASS_TYPE_ID && schoolYearStart(r.date) === cy
    ).length;
  }, [reports]);

  // Build the list of school years present across both report sets.
  const availableYears = React.useMemo(() => {
    const set = new Set();
    [...(reports || []), ...(accidentReports || [])].forEach((r) => {
      const sy = schoolYearStart(r.date);
      if (sy != null) set.add(sy);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [reports, accidentReports]);

  const filterAndSort = (list) => {
    const filtered = (list || []).filter((r) => {
      if (schoolYear === "all") return true;
      return String(schoolYearStart(r.date)) === String(schoolYear);
    });
    return filtered.sort((a, b) => {
      const da = a.date || "";
      const dbb = b.date || "";
      return sortOrder === "newest" ? dbb.localeCompare(da) : da.localeCompare(dbb);
    });
  };

  const filteredReports = React.useMemo(
    () => filterAndSort(reports),
    [reports, schoolYear, sortOrder] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredAccidents = React.useMemo(
    () => filterAndSort(accidentReports),
    [accidentReports, schoolYear, sortOrder] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const yearText = schoolYear === "all" ? "any year" : schoolYearLabel(parseInt(schoolYear, 10));

  const FIELD_LABELS = {
    date: "Date", time: "Time", type: "Report Type", incident_type: "Incident Type",
    description: "Description", action_taken: "Action Taken", witnesses: "Staff Witness",
    follow_up_needed: "Follow-up Needed", visibility: "Visibility",
  };
  const fmtVal = (v) => {
    if (v === true) return "Yes";
    if (v === false) return "No";
    if (Array.isArray(v)) return v.join(", ") || "—";
    return v === "" || v == null ? "—" : String(v);
  };

  const openEdit = (report) => {
    setEditReport(report);
    setForm({
      date: report.date || "",
      time: report.time || "",
      type: report.type || "clinical",
      incident_type: report.incident_type || "",
      description: report.description || "",
      action_taken: report.action_taken || "",
      witnesses: report.witnesses || "",
      follow_up_needed: !!report.follow_up_needed,
      visibility: report.visibility || ["Admin"],
    });
  };

  const toggleVisibility = (role) => {
    setForm((p) => ({
      ...p,
      visibility: p.visibility.includes(role)
        ? p.visibility.filter((r) => r !== role)
        : [...p.visibility, role],
    }));
  };

  const handleSave = async () => {
    if (!form.description?.trim()) {
      toast.error("Description is required");
      return;
    }
    setSaving(true);
    try {
      await updateIncidentReport(editReport.id, form);
      toast.success("Incident report updated");
      setEditReport(null);
      setForm(null);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update report");
    }
    setSaving(false);
  };

  const handlePrintReport = async (report) => {
    const studentName = `${student.first_name} ${student.last_name}`;
    const reportTypeLabels = {
      clinical: "Clinical Report",
      accident: "Accident Report",
      general_note: "General Note",
      incident: "Incident Report",
    };
    const badgeColors = {
      clinical: "background: #f3e8ff; color: #7e22ce;",
      accident: "background: #fee2e2; color: #dc2626;",
      general_note: "background: #f3f4f6; color: #374151;",
    };

    // Pull attachments so the printout includes them (images embedded, docs listed).
    let attachmentsHtml = "";
    try {
      const atType = report.type === "accident" ? "accident_student" : report.type;
      const { data: atts } = await listReportAttachments(atType, report.id);
      if ((atts || []).length) {
        const rows = atts.map((a) => {
          const isImg = /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(a.filename || "");
          const url = reportAttachmentViewUrl(a.id);
          const caption = `${a.filename}${a.description ? ` — ${a.description}` : ""} <span style="color:#888">(${a.uploaded_by_name})</span>`;
          return isImg
            ? `<div style="margin-bottom:12px;"><img src="${url}" style="max-width:100%;max-height:360px;border:1px solid #ddd;border-radius:6px;" /><div style="font-size:12px;color:#555;margin-top:4px;">${caption}</div></div>`
            : `<div style="margin-bottom:6px;font-size:13px;">• ${caption}</div>`;
        }).join("");
        attachmentsHtml = `<div class="section"><div class="section-title">Attachments (${atts.length})</div><div class="section-content">${rows}</div></div>`;
      }
    } catch { /* ignore attachment load errors in print */ }

    const content = `
      <div style="text-align: center; margin-bottom: 16px;">
        <span class="badge" style="${badgeColors[report.type] || ""}">${report.type}</span>
      </div>
      <div class="grid-2">
        <div><div class="field-label">Student Name</div><div class="field-value">${studentName}</div></div>
        <div><div class="field-label">Date of Birth</div><div class="field-value">${student.dob || "Not specified"}</div></div>
        <div><div class="field-label">Report Date</div><div class="field-value">${report.date || "Not specified"}</div></div>
        <div><div class="field-label">Report Time</div><div class="field-value">${report.time || "Not specified"}</div></div>
      </div>
      <div class="section">
        <div class="section-title">Description</div>
        <div class="section-content">${report.description || "No description provided"}</div>
      </div>
      ${report.action_taken ? `<div class="section"><div class="section-title">Action Taken</div><div class="section-content">${report.action_taken}</div></div>` : ""}
      ${report.witnesses ? `<div class="section"><div class="section-title">Witnesses</div><div class="section-content">${report.witnesses}</div></div>` : ""}
      ${attachmentsHtml}
      <div class="grid-2">
        <div><div class="field-label">Follow-up Needed</div><div class="field-value">${report.follow_up_needed ? "Yes" : "No"}</div></div>
        <div><div class="field-label">Visibility</div><div class="field-value">${report.visibility?.join(", ") || "Admin"}</div></div>
      </div>
      <div class="print-footer" style="margin-top:30px;">
        <p>Report Created: ${report.created_at ? new Date(report.created_at).toLocaleDateString() : "Not available"}</p>
      </div>
    `;

    printPage({
      title: reportTypeLabels[report.type] || "REPORT",
      subtitle: "SMARTIS — Student File",
      content,
      extraStyles: `body { max-width: 800px; margin: 0 auto; padding: 40px; }`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#00305E]/10 text-[#00305E] px-3 py-1.5 text-sm font-medium" data-testid="class-moves-count">
        Class moves caused this year: <span className="font-bold" data-testid="class-moves-count-value">{classMovesThisYear}</span>
      </div>
      {(reports.length > 0 || accidentReports.length > 0) && (
        <div className="flex flex-wrap items-center gap-3" data-testid="incidents-toolbar">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">School Year</Label>
            <Select value={schoolYear} onValueChange={setSchoolYear}>
              <SelectTrigger className="w-[150px] h-9" data-testid="incidents-year-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {availableYears.map((y) => (
                  <SelectItem key={y} value={String(y)}>{schoolYearLabel(y)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Sort</Label>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[150px] h-9" data-testid="incidents-sort-order">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      {canEdit && (
        <Card>
          <CardHeader>
            <CardTitle>Clinical & Incident Reports</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredReports.length > 0 ? (
              <div className="space-y-3">
                {filteredReports.map((r, i) => (
                  <div key={r.id || i} className="p-4 rounded-lg border">
                    <div className="flex justify-between mb-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        r.type === "clinical" ? "bg-purple-100 text-purple-800" :
                        r.type === "accident" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"
                      }`}>
                        {r.type}{r.incident_type ? ` — ${typeLabel(r.incident_type)}` : ""}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{r.date}</span>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} title="Edit report" data-testid={`edit-report-${i}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"/>
                          </svg>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handlePrintReport(r)} title="Print report" data-testid={`print-report-${i}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/>
                          </svg>
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{r.description}</p>
                    {r.incident_type === MOVED_CLASS_TYPE_ID && r.group_name && (
                      <p className="text-xs text-[#00305E] mt-1 font-medium" data-testid={`incident-group-info-${i}`}>
                        Group moved: {r.group_name}
                        {r.period_time ? ` · ${r.period_time}` : ""}
                        {r.moved_to ? ` · to ${r.moved_to}` : ""}
                        {r.duration_minutes != null && r.duration_minutes !== "" ? ` · ${r.duration_minutes} min` : ""}
                      </p>
                    )}
                    {r.action_taken && <p className="text-xs text-muted-foreground mt-2"><span className="font-medium">Action taken:</span> {r.action_taken}</p>}
                    {r.edit_history?.length > 0 && (
                      <div className="mt-2 border-t pt-2">
                        <button
                          type="button"
                          className="text-[11px] text-[#00305E] underline"
                          onClick={() => setOpenHistory((o) => ({ ...o, [r.id]: !o[r.id] }))}
                          data-testid={`report-history-toggle-${i}`}
                        >
                          {openHistory[r.id] ? "Hide" : "Show"} change history ({r.edit_history.length})
                        </button>
                        {openHistory[r.id] && (
                          <div className="mt-2 space-y-2" data-testid={`report-history-${i}`}>
                            {[...r.edit_history].reverse().map((h, hi) => (
                              <div key={hi} className="text-[11px] bg-gray-50 border rounded p-2">
                                <div className="font-medium text-gray-700">
                                  {h.edited_by_name || "Unknown"} · {h.edited_at ? new Date(h.edited_at).toLocaleString() : ""}
                                </div>
                                <ul className="mt-1 space-y-0.5">
                                  {(h.changes || []).map((c, ci) => (
                                    <li key={ci} className="text-gray-600">
                                      <span className="font-medium">{FIELD_LABELS[c.field] || c.field}:</span>{" "}
                                      <span className="line-through text-red-500">{fmtVal(c.old)}</span>{" → "}
                                      <span className="text-emerald-600">{fmtVal(c.new)}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-3 border-t pt-3">
                      <ReportAttachments reportType={r.type === "accident" ? "accident_student" : r.type} reportId={r.id} isAdmin={hasRole("Admin")} canManage={canEdit} compact />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4" data-testid="incidents-empty-filtered">
                {(reports.length || accidentReports.length) ? `No incident reports for ${yearText}` : "No incident reports"}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {filteredAccidents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
              </svg>
              Accident Reports
            </CardTitle>
            <CardDescription>Filed from Reports & Reporting page</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredAccidents.map((r) => (
                <div key={r.id} className="p-4 rounded-lg border border-red-200 bg-red-50/50">
                  <div className="flex justify-between mb-2">
                    <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">Accident Report</span>
                    <span className="text-sm text-muted-foreground">{r.date}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm mt-2">
                    <div><span className="text-muted-foreground">Place:</span> {r.place_of_accident}</div>
                    <div><span className="text-muted-foreground">Time:</span> {r.time}</div>
                    <div><span className="text-muted-foreground">Medical Response:</span> <span className={`px-1.5 py-0.5 rounded text-xs ${r.medical_response === 'ambulance' ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'}`}>{r.medical_response}</span></div>
                    {r.witness_name && <div><span className="text-muted-foreground">Witness:</span> {r.witness_name}</div>}
                  </div>
                  <div className="mt-2 text-sm"><span className="text-muted-foreground">Incident:</span> {r.describe_incident}</div>
                  <div className="mt-1 text-sm"><span className="text-muted-foreground">Injury:</span> {r.describe_injury}</div>
                  {r.follow_up && <div className="mt-1 text-sm"><span className="text-muted-foreground">Follow-up:</span> {r.follow_up}</div>}
                  <div className="mt-3 border-t border-red-200 pt-3">
                    <ReportAttachments reportType="accident_student" reportId={r.id} isAdmin={hasRole("Admin")} canManage={canEdit} compact />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!canEdit && !filteredReports.length && !filteredAccidents.length && (
        <Card>
          <CardContent className="py-8">
            <p className="text-muted-foreground text-center" data-testid="incidents-empty-all">
              {(reports.length || accidentReports.length)
                ? `No incidents or accident reports for ${yearText}`
                : "No incidents or accident reports on file"}
            </p>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editReport} onOpenChange={(o) => { if (!o) { setEditReport(null); setForm(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-incident-dialog">
          <DialogHeader>
            <DialogTitle>Edit Report</DialogTitle>
            <DialogDescription>Update this report's details. Involved-student links are set at creation and aren't changed here.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Report Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
                  <SelectTrigger data-testid="edit-incident-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{REPORT_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              {form.type === "incident" && (
                <div className="space-y-2">
                  <Label>Type of Incident</Label>
                  <Select value={form.incident_type} onValueChange={(v) => setForm((p) => ({ ...p, incident_type: v }))}>
                    <SelectTrigger data-testid="edit-incident-category"><SelectValue placeholder="Select incident type" /></SelectTrigger>
                    <SelectContent>
                      {incidentTypes.filter((t) => !t.archived || t.id === form.incident_type).map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.label}{t.archived ? " (archived)" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="edit-incident-date" /></div>
                <div className="space-y-2"><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm((p) => ({ ...p, time: e.target.value }))} data-testid="edit-incident-time" /></div>
              </div>
              <div className="space-y-2"><Label>Description *</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="edit-incident-description" /></div>
              <div className="space-y-2"><Label>Action Taken</Label><Textarea rows={2} value={form.action_taken} onChange={(e) => setForm((p) => ({ ...p, action_taken: e.target.value }))} data-testid="edit-incident-action" /></div>
              <div className="space-y-2"><Label>Staff Witness</Label><Input value={form.witnesses} onChange={(e) => setForm((p) => ({ ...p, witnesses: e.target.value }))} placeholder="Names of staff witnesses" data-testid="edit-incident-witnesses" /></div>
              <div className="flex items-center space-x-2">
                <Checkbox id="edit-followup" checked={form.follow_up_needed} onCheckedChange={(c) => setForm((p) => ({ ...p, follow_up_needed: !!c }))} />
                <Label htmlFor="edit-followup" className="cursor-pointer">Follow-up Needed</Label>
              </div>
              <div className="space-y-2">
                <Label>Visibility</Label>
                <div className="flex gap-4">
                  {["Admin", "Clinician", "All Staff"].map((role) => (
                    <div key={role} className="flex items-center space-x-2">
                      <Checkbox id={`edit-vis-${role}`} checked={form.visibility.includes(role)} onCheckedChange={() => toggleVisibility(role)} />
                      <Label htmlFor={`edit-vis-${role}`} className="cursor-pointer">{role}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditReport(null); setForm(null); }} disabled={saving}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={handleSave} disabled={saving} data-testid="save-incident-edit-btn">
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
